import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import LandingShell from '@/components/landing/LandingShell';
import SEO from '@/components/common/SEO';
import Breadcrumbs from '@/components/semantic/Breadcrumbs';
import FaqSection from '@/components/semantic/FaqSection';
import RelatedLinksSection from '@/components/semantic/RelatedLinksSection';
import SemanticFooter from '@/components/semantic/SemanticFooter';
import SemanticNavHub from '@/components/semantic/SemanticNavHub';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { docsAppDirectoryEntries } from '@/lib/semantic-content/appDirectory';
import { semanticNavigationGroups } from '@/lib/semantic-content/navigation';
import { docsHubPage, resolveSemanticLinks } from '@/lib/semantic-content/pages';
import { getBreadcrumbItems } from '@/lib/semantic-content/breadcrumbs';
import { buildSemanticStructuredData } from '@/lib/semantic-content/schema';

export default function Docs() {

  const [navSearch, setNavSearch] = useState('');
  const breadcrumbItems = getBreadcrumbItems(docsHubPage);
  const quickLinks = resolveSemanticLinks(docsHubPage.quickLinkPaths);
  const relatedLinks = resolveSemanticLinks(docsHubPage.relatedLinkPaths);
  const hubGroups = docsHubPage.hubGroups.map((group) => ({
    ...group,
    resolvedLinks: resolveSemanticLinks(group.links),
  }));

  const normalizedSearch = navSearch.trim().toLowerCase();
  const filteredNavigationGroups = useMemo(() => {
    if (!normalizedSearch) {
      return semanticNavigationGroups;
    }

    const matchingSections = semanticNavigationGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const label = item.label.toLowerCase();
          const groupTitle = group.title.toLowerCase();
          const path = item.path.toLowerCase();

          return label.includes(normalizedSearch) || groupTitle.includes(normalizedSearch) || path.includes(normalizedSearch);
        }),
      }))
      .filter((group) => group.items.length > 0);

    const matchingApps = docsAppDirectoryEntries.filter((entry) => {
      const haystack = [entry.label, entry.description, entry.folder, entry.slug].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });

    const appGroup = matchingApps.length
      ? [
          {
            title: 'Apps',
            items: matchingApps.map((entry) => ({
              path: entry.path,
              label: entry.label,
            })),
          },
        ]
      : [];

    return [...appGroup, ...matchingSections];
  }, [normalizedSearch]);

  return (
    <LandingShell>
      <SEO
        title={docsHubPage.title}
        description={docsHubPage.description}
        ogTitle={docsHubPage.ogTitle}
        ogDescription={docsHubPage.ogDescription}
        url={docsHubPage.path}
        type="article"
        structuredData={buildSemanticStructuredData(docsHubPage)}

      />

      <div className="bg-background text-foreground">
        <section aria-labelledby="docs-semantic-menu" className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8">
          <div className="relative z-20 -mb-8 overflow-hidden rounded-[24px] border border-border/60 bg-card/70 shadow-[0_24px_80px_-40px_rgba(0,0,0,0.75)] backdrop-blur-sm sm:-mb-10 lg:-mb-14 xl:-mb-16">
            <div className="grid gap-5 border-b border-border/60 px-5 py-5 sm:px-6 md:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:items-start lg:gap-8">
              <div>
                <h2 id="docs-semantic-menu" className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                  Browse MapleOS by section
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground md:text-base">
                  Quickly learn about MapleOS, use cases, comparisons, and the app catalog.
                </p>
              </div>

              <div className="w-full lg:justify-self-end">
                <label htmlFor="docs-nav-search" className="mb-2 block text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground/80 sm:text-xs">
                  Search docs and apps
                </label>
                <div className="group relative overflow-hidden rounded-2xl border border-primary/20 bg-background/80 shadow-[0_12px_40px_-24px_rgba(59,130,246,0.55)] transition-colors focus-within:border-primary/40">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" />
                  <Input
                    id="docs-nav-search"
                    value={navSearch}
                    onChange={(event) => setNavSearch(event.target.value)}
                    placeholder="Search Music, AI Center, Code Editor, concepts, or use cases"
                    className="h-11 border-0 bg-transparent pl-11 pr-11 text-sm shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0 sm:h-12"
                  />
                  {navSearch ? (
                    <button
                      type="button"
                      onClick={() => setNavSearch('')}
                      className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                      aria-label="Clear navigation search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {filteredNavigationGroups.length > 0 ? (
              <div className="grid gap-px bg-border/60 sm:grid-cols-2 xl:grid-cols-4">
                {filteredNavigationGroups.map((group) => (

                  <div key={group.title} className="bg-card/90 p-5 sm:p-6">
                    <h3 className="text-base font-semibold text-foreground sm:text-lg">{group.title}</h3>
                    <ul className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
                      {group.items.map((item) => (
                        <li key={item.path}>
                          <Link to={item.path} className="block text-sm font-medium text-foreground transition-colors hover:text-primary md:text-base">
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-10 text-center md:px-8">
                <p className="text-base font-medium text-foreground">No navigation matches for "{navSearch.trim()}".</p>
                <p className="mt-2 text-sm text-muted-foreground">Try a page name like Music, AI Center, Consumers, or Knowledge Base.</p>
              </div>
            )}
          </div>
        </section>

        <main className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 pb-10 pt-16 sm:px-6 sm:pt-20 lg:px-8 lg:pb-14 lg:pt-24 xl:pt-28">
          <article className="space-y-10">
            <header className="space-y-6 rounded-3xl border border-border/60 bg-card/60 p-6 md:p-8">
              <Breadcrumbs items={breadcrumbItems} />
              <div className="space-y-4">
                <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary">
                  {docsHubPage.eyebrow}
                </Badge>
                <div className="space-y-4">
                  <h1 className="max-w-4xl text-4xl font-semibold tracking-tight md:text-5xl">{docsHubPage.title}</h1>
                  <p className="max-w-3xl text-lg leading-8 text-muted-foreground">{docsHubPage.summary}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-4 sm:px-5">
                <p className="text-sm leading-7 text-foreground/90 sm:text-base">
                  <span className="font-semibold text-foreground">Using MapleOS with ChatGPT, Claude, or another AI product?</span>{' '}
                  Give it access to this link:{' '}
                  <a
                    href="https://mapleos.ai/llms.txt"
                    className="font-medium text-primary underline underline-offset-4 transition-colors hover:text-primary/80"
                  >
                    https://mapleos.ai/llms.txt
                  </a>
                </p>
              </div>

              <section aria-labelledby="docs-quick-links" className="space-y-4">
                <div>
                  <h2 id="docs-quick-links" className="text-lg font-semibold text-foreground">
                    Start here
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Use these pages to move from top-level MapleOS explainers into deeper app, concept, and use-case documentation.
                  </p>
                </div>
                <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {quickLinks.map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className="block rounded-xl border border-border/60 bg-background/80 px-4 py-3 font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            </header>

            <div className="grid gap-6 md:grid-cols-2">
              {docsHubPage.sections.map((section) => (
                <Card key={section.id} className="border-border/60 bg-card/60 shadow-none">
                  <CardHeader>
                    <CardTitle className="text-2xl">{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-base leading-7 text-muted-foreground">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>

            <SemanticNavHub groups={hubGroups} />

            <FaqSection items={docsHubPage.faq} />

            <RelatedLinksSection
              title="Related MapleOS pages"
              description="These additional pages help connect the docs hub the dots with MapleOS explainers, concepts, and practical workflows."
              links={relatedLinks}
            />
          </article>
        </main>
        <SemanticFooter />
      </div>
    </LandingShell>
  );
}